import { useCallback } from 'react';
import { Button, DrawerSection, TextArea, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { notifyUcpOpened } from '../../../utils/toastNotify.js';
import { DetailField } from '../shared/index.js';
import { ExternalLink } from 'lucide-react';

/**
 * What the agent prepared: the draft first (as in the prototype), then the
 * recipient / subject / channel it would go out with. The customer webchat
 * variant adds a "View full UCP profile" link in the section header.
 *
 * "Edit then send" revises the draft in place: the box turns into a text area
 * and the decision footer swaps to Cancel / Confirm Send.
 *
 * UCP is mock-only until CT-4867: the button toasts instead of navigating.
 */
function PreparedOutputBlock({ detail }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { editing, draft, setDraft } = useEventDraft();
  const output = detail?.output;
  const ucpProfileUrl = detail?.ucpProfileUrl;

  const handleViewUcp = useCallback(() => {
    notifyUcpOpened(toast, t, ucpProfileUrl);
  }, [toast, t, ucpProfileUrl]);

  const handleDraftChange = useCallback(
    (event) => {
      setDraft(event.target.value);
    },
    [setDraft],
  );

  if (!output) return null;

  const rows = [
    { label: t('continuation.recipient'), value: output.recipient },
    { label: t('continuation.subject'), value: output.subject },
    { label: t('continuation.channel'), value: output.channel },
  ].filter((row) => row.value);

  return (
    <DrawerSection
      title={t('continuation.whatAgentPrepared')}
      rightContent={
        ucpProfileUrl ? (
          <Button variant="ghost" size="sm" rightIcon={ExternalLink} onClick={handleViewUcp}>
            {t('continuation.viewUcp')}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        {editing ? (
          <TextArea
            rows={10}
            value={draft}
            onChange={handleDraftChange}
            aria-label={t('continuation.editDraft')}
          />
        ) : (
          <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg border border-edge bg-surface-muted px-4 py-3 font-sans text-sm leading-relaxed text-content-secondary scrollbar-aimsos-auto-sm">
            {output.draft}
          </pre>
        )}

        {rows.length > 0 ? (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {rows.map((row) => (
              <DetailField key={row.label} label={row.label} value={row.value} />
            ))}
          </dl>
        ) : null}
      </div>
    </DrawerSection>
  );
}

export default PreparedOutputBlock;
